/**
 * The selected trip, as the Main board's route inspector draws it, read only:
 * the vehicle, the driver slot, when it leaves and is back, what it carries,
 * and its stops in order with their windows.
 *
 * Whether a window holds is said in words ("Holds", "Missed"), in a Badge
 * whose tone repeats it: never colour alone (WCAG 1.4.1).
 */
import { Badge, Section, Table } from "@depicomnlbv/platform-ui";

import type { Day, Stop, Trip } from "../api";
import {
  formatClock,
  formatDuration,
  formatKilometres,
  formatLitres,
} from "../time";
import { tripLabels, vehicleName } from "../trips";

const NOT_RECORDED = "Not recorded";

function operator(trip: Trip): string {
  if (trip.vehicle.external === null) return NOT_RECORDED;
  return trip.vehicle.external ? "External haulier" : "Own fleet";
}

function windowText(stop: Stop, day: Day): string {
  const clock = (iso: string) => formatClock(iso, day.time_zone, day.date);
  if (stop.window_earliest && stop.window_latest) {
    return `${clock(stop.window_earliest)} to ${clock(stop.window_latest)}`;
  }
  if (stop.window_earliest) return `from ${clock(stop.window_earliest)}`;
  if (stop.window_latest) return `until ${clock(stop.window_latest)}`;
  return "None";
}

function Fact({ name, children }: { name: string; children: string }) {
  return (
    <div className="facts__row">
      <dt>{name}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Empty({ children }: { children: string }) {
  return <Section empty={children} title="Trip inspector" />;
}

export function TripInspector({
  day,
  selected,
}: {
  day: Day;
  /** The `trip` parameter of the address as it stands, or null without one. */
  selected: string | null;
}) {
  if (day.trips.length === 0) return <Empty>There is no trip to inspect on this day.</Empty>;
  if (selected === null) {
    return <Empty>Select a trip in the list or on the timeline to see its vehicle and stops.</Empty>;
  }
  const trip = day.trips.find((t) => String(t.route_id) === selected);
  if (!trip) return <Empty>{`There is no trip ${selected} on this day.`}</Empty>;

  const title = `${tripLabels(day.trips).get(trip.route_id)} · ${vehicleName(trip.vehicle)}`;
  const clock = (iso: string | null) =>
    iso === null ? NOT_RECORDED : formatClock(iso, day.time_zone, day.date);

  return (
    <Section title={title}>
      <dl className="facts">
        <Fact name="Registration">{trip.vehicle.registration ?? NOT_RECORDED}</Fact>
        <Fact name="Trailer">{trip.vehicle.trailer_registration ?? "None"}</Fact>
        <Fact name="Type">{trip.vehicle.vehicle_type ?? NOT_RECORDED}</Fact>
        <Fact name="Capacity">
          {trip.vehicle.capacity_litres === null ? NOT_RECORDED : formatLitres(trip.vehicle.capacity_litres)}
        </Fact>
        <Fact name="Operated by">{operator(trip)}</Fact>
        {/* The planner's slot number, as the findings name it ("Driver 1"):
            not a person, who is not assigned in the plan. */}
        <Fact name="Driver slot">{trip.driver_slot === null ? NOT_RECORDED : String(trip.driver_slot)}</Fact>
        <Fact name="Departs">{clock(trip.departure)}</Fact>
        <Fact name="Back at factory">{clock(trip.return)}</Fact>
        <Fact name="Load">{trip.litres === null ? NOT_RECORDED : formatLitres(trip.litres)}</Fact>
        <Fact name="Distance">{trip.distance_km === null ? NOT_RECORDED : formatKilometres(trip.distance_km)}</Fact>
        <Fact name="Duration">{trip.duration_h === null ? NOT_RECORDED : formatDuration(trip.duration_h)}</Fact>
        <Fact name="Road route">
          {trip.legs.length > 0 ? "Routed" : "Not routed: the times are the planner's own"}
        </Fact>
      </dl>

      {/* A section in a section, as the block allows: its title is an h4.
          The table is the block's own scroll box, so seven columns scroll in
          here on a phone and the page does not (WCAG 1.4.10). */}
      <Section
        empty={trip.stops.length === 0 ? "This trip has no stops." : undefined}
        flush
        headingLevel={4}
        title="Stops"
      >
        <Table caption={`Stops of ${title}, in order`} captionHidden>
          <thead>
            <tr>
              <th className="dpc-table__num" scope="col">
                Stop
              </th>
              <th scope="col">Farm</th>
              <th scope="col">Arrives</th>
              <th scope="col">Leaves</th>
              <th className="dpc-table__num" scope="col">
                Service
              </th>
              <th scope="col">Window</th>
              <th scope="col">Window holds</th>
            </tr>
          </thead>
          <tbody>
            {trip.stops.map((stop) => (
              <tr key={stop.stop_seq}>
                <th className="dpc-table__num" scope="row">
                  {stop.stop_seq}
                </th>
                <td>{stop.farm_name ?? `Farmer ${stop.farmer_id}`}</td>
                <td>{clock(stop.arrival)}</td>
                <td>{clock(stop.departure)}</td>
                <td className="dpc-table__num">
                  {stop.service_min === null ? NOT_RECORDED : `${stop.service_min} min`}
                </td>
                <td>{windowText(stop, day)}</td>
                <td>
                  {/* Nothing stored, nothing to hold: the planner still
                      writes window_ok true for such a stop, and "Holds"
                      would claim a check that had nothing to check. */}
                  {stop.window_earliest === null && stop.window_latest === null ? (
                    "No window stored"
                  ) : (
                    <Badge tone={stop.window_ok ? "done" : "critical"}>
                      {stop.window_ok ? "Holds" : "Missed"}
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>
    </Section>
  );
}
